import { Injectable, inject, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SocketService } from './socket.service';
import { UiService } from './ui.service';
import { AuthService } from './auth.service';

export interface DashNotification {
  id: string;
  type: 'order_new' | 'order_cancelled' | 'support_new' | 'support_assigned' | 'support_escalated' | 'support_replied' | 'support_resolved' | 'support_status_changed' | 'support_priority_changed' | 'inventory_low_stock' | 'product_sold_out' | 'product_restored';
  title: string;
  body: string;
  ticketId?: number;
  ticketNumber?: string;
  route?: string;
  readAt: number | null;
  createdAt: number;
}

const MAX_ITEMS = 30;

@Injectable({ providedIn: 'root' })
export class DashboardNotificationsService implements OnDestroy {
  private socketService = inject(SocketService);
  private uiService    = inject(UiService);
  private authService  = inject(AuthService);
  private router       = inject(Router);

  private _notifications$ = new BehaviorSubject<DashNotification[]>([]);
  readonly notifications$ = this._notifications$.asObservable();

  private subs: Subscription[] = [];
  private connected = false;
  private readonly logoutHandler = () => this.resetRealtime();

  constructor() {
    window.addEventListener('vlug:auth-logout', this.logoutHandler);
  }

  get unreadCount(): number {
    return this._notifications$.value.filter(n => !n.readAt).length;
  }

  get unreadCount$() {
    return new BehaviorSubject<number>(this.unreadCount).asObservable();
  }

  get notifications(): DashNotification[] {
    return this._notifications$.value;
  }

  init(): void {
    if (this.connected) return;
    const user = this.authService.getUser();
    if (!user || user._role !== 'ADMIN') return;

    this.connected = true;
    this.socketService.connect({}, 'user');
    this.socketService.emit('support:admin:join', {}, 'user');
    this.socketService.emit('orders:admin:join', {}, 'user');

    // Re-join on every reconnect so room membership survives network drops
    this.subs.push(
      this.socketService.onConnect('user').subscribe(() => {
        this.socketService.emit('support:admin:join', {}, 'user');
        this.socketService.emit('orders:admin:join', {}, 'user');
      })
    );

    this.subs.push(
      this.socketService.on<any>('order:new', 'user').subscribe(data => {
        this._push({
          type: 'order_new',
          title: `New order — #${data.order_ref ?? ''}`,
          body: `${data.site_name ?? 'A store'} received a new order · €${data.total ?? ''}`,
          route: '/admin/orders',
        });
        this.uiService.info(`New order #${data.order_ref ?? ''} at ${data.site_name ?? ''}`);
        this._refreshBadge();
      })
    );

    this.subs.push(
      this.socketService.on<any>('support.ticket.created', 'user').subscribe(data => {
        this._push({
          type: 'support_new',
          title: `New ticket — ${data.ticket_number ?? ''}`,
          body: data.subject ?? 'A new support request was submitted.',
          ticketId: data.id,
          ticketNumber: data.ticket_number,
          route: '/admin/support',
        });
        this.uiService.info(`New support ticket: ${data.subject ?? data.ticket_number}`);
        this._refreshBadge();
      }),

      this.socketService.on<any>('support.ticket.escalated', 'user').subscribe(data => {
        this._push({
          type: 'support_escalated',
          title: `⚠ Escalated — ${data.ticket_number ?? ''}`,
          body: data.subject ?? 'A ticket has been escalated and needs attention.',
          ticketId: data.id,
          ticketNumber: data.ticket_number,
          route: '/admin/support',
        });
        this.uiService.warning(`Ticket escalated: ${data.subject ?? data.ticket_number}`);
        this._refreshBadge();
      }),

      this.socketService.on<any>('support.message.created', 'user').subscribe(data => {
        const { senderRole, isInternal, preview } = this._parseMessagePayload(data);

        // Internal notes are admin-only, and admin-authored replies (including
        // the sender's own) must never surface as a customer-reply alert.
        if (isInternal || senderRole === 'admin') return;

        this._push({
          type: 'support_replied',
          title: `Reply on ${data.ticket_number ?? 'ticket'}`,
          body: preview,
          ticketId: data.ticket_id,
          ticketNumber: data.ticket_number,
          route: '/admin/support',
        });
        this.uiService.info('notifications.new_support_reply', { ticket: data.ticket_number ?? '' });
        this._refreshBadge();
      }),

      this.socketService.on<any>('support.ticket.status_changed', 'user').subscribe(data => {
        // Backend sends the new status as `new_status`; mirrors SupportService's parsing.
        const status = data.new_status ?? data.status;
        const ticketId = data.ticket_id ?? data.id;
        if (status === 'resolved' || status === 'closed') {
          this._push({
            type: 'support_resolved',
            title: `Ticket resolved — ${data.ticket_number ?? ''}`,
            body: `Status changed to ${status}.`,
            ticketId,
            ticketNumber: data.ticket_number,
            route: '/admin/support',
          });
          this.uiService.success(`Ticket ${data.ticket_number ?? ''} marked as ${status}.`);
        } else {
          this._push({
            type: 'support_status_changed',
            title: `Status updated — ${data.ticket_number ?? ''}`,
            body: `Status changed to ${status}.`,
            ticketId,
            ticketNumber: data.ticket_number,
            route: '/admin/support',
          });
          this.uiService.info(`Ticket ${data.ticket_number ?? ''} status changed to ${status}.`);
        }
        this._refreshBadge();
      }),

      this.socketService.on<any>('support.ticket.updated', 'user').subscribe(data => {
        this._push({
          type: 'support_priority_changed',
          title: `Ticket updated — ${data.ticket_number ?? ''}`,
          body: data.priority != null ? `Priority changed to ${data.priority}.` : 'Ticket details were updated.',
          ticketId: data.ticket_id ?? data.id,
          ticketNumber: data.ticket_number,
          route: '/admin/support',
        });
        this.uiService.info(`Ticket ${data.ticket_number ?? ''} updated${data.priority != null ? ` — priority: ${data.priority}` : ''}.`);
        this._refreshBadge();
      }),
    );

    this.subs.push(
      this.socketService.on<any>('order:cancelled', 'user').subscribe(data => {
        this._push({
          type: 'order_cancelled',
          title: `Order cancelled — #${data.order_ref ?? ''}`,
          body: `${data.site_name ?? 'A store'} order was cancelled · €${data.total ?? ''}`,
          route: '/admin/orders',
        });
        this.uiService.warning(`Order #${data.order_ref ?? ''} cancelled at ${data.site_name ?? ''}`);
        this._refreshBadge();
      }),

      this.socketService.on<any>('support.ticket.assigned', 'user').subscribe(data => {
        this._push({
          type: 'support_assigned',
          title: `Ticket assigned — ${data.ticket_number ?? ''}`,
          body: data.subject ?? 'A support ticket has been assigned.',
          ticketId: data.id,
          ticketNumber: data.ticket_number,
          route: '/admin/support',
        });
        this.uiService.info(`Ticket ${data.ticket_number ?? ''} assigned.`);
        this._refreshBadge();
      }),

      this.socketService.on<any>('inventory:low_stock', 'user').subscribe(data => {
        this._push({
          type: 'inventory_low_stock',
          title: `Low stock — ${data.ingredient_name ?? ''}`,
          body: `${data.site_name ?? 'A store'}: ${data.current_stock} ${data.unit ?? ''} remaining (min: ${data.min_stock_alert ?? ''})`,
          route: '/admin/inventory',
        });
        this.uiService.warning(`Low stock: ${data.ingredient_name ?? ''} at ${data.site_name ?? ''}`);
        this._refreshBadge();
      }),

      this.socketService.on<any>('product:auto_sold_out', 'user').subscribe(data => {
        this._push({
          type: 'product_sold_out',
          title: `Products auto sold-out`,
          body: `${data.site_name ?? 'A store'}: ${data.ingredient_name ?? ''} depleted — ${(data.product_ids ?? []).length} product(s) hidden`,
          route: '/admin/inventory',
        });
        this.uiService.warning(`Products sold out at ${data.site_name ?? ''} — ${data.ingredient_name ?? ''} depleted`);
        this._refreshBadge();
      }),

      this.socketService.on<any>('product:auto_restored', 'user').subscribe(data => {
        this._push({
          type: 'product_restored',
          title: `Products restored`,
          body: `${data.site_name ?? 'A store'}: ${data.ingredient_name ?? ''} restocked — ${(data.product_ids ?? []).length} product(s) available again`,
          route: '/admin/inventory',
        });
        this.uiService.success(`Products restored at ${data.site_name ?? ''} — ${data.ingredient_name ?? ''} back in stock`);
        this._refreshBadge();
      }),
    );
  }

  markAllRead(): void {
    const now = Date.now();
    this._notifications$.next(
      this._notifications$.value.map(n => ({ ...n, readAt: n.readAt ?? now }))
    );
  }

  markRead(id: string): void {
    this._notifications$.next(
      this._notifications$.value.map(n => n.id === id ? { ...n, readAt: Date.now() } : n)
    );
  }

  clearAll(): void {
    this._notifications$.next([]);
  }

  navigateTo(notif: DashNotification): void {
    this.markRead(notif.id);
    if (notif.route) this.router.navigate([notif.route]);
  }

  private _push(partial: Omit<DashNotification, 'id' | 'readAt' | 'createdAt'>): void {
    const notif: DashNotification = {
      ...partial,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      readAt: null,
      createdAt: Date.now(),
    };
    const current = this._notifications$.value;
    this._notifications$.next([notif, ...current].slice(0, MAX_ITEMS));
  }

  /** Mirrors SupportService's flat-vs-object payload normalisation for `support.message.created`. */
  private _parseMessagePayload(data: any): { senderRole: string; isInternal: boolean; preview: string } {
    const msg = data.message && typeof data.message === 'object' ? data.message : null;
    const isInternal = !!(msg ? msg.is_internal_note : data.is_internal_note);
    const senderRole = (msg ? msg.sender_role : data.sender_role) ?? 'system';
    const text = msg ? msg.message : (data.text ?? data.message ?? '');
    return { senderRole, isInternal, preview: String(text ?? '').slice(0, 80) };
  }

  private _refreshBadge(): void {
    // Trigger sidebar badge re-poll immediately (it normally polls every 60s)
    // Emitting auth change causes SidebarComponent to call getBadges()
    this.authService.emitChange();
  }

  private resetRealtime(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
    this.connected = false;
    this.clearAll();
  }

  ngOnDestroy(): void {
    window.removeEventListener('vlug:auth-logout', this.logoutHandler);
    this.resetRealtime();
  }
}
